import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { adminApi, errorMessage, session, setExpiredHandler, type UserProfile } from './api/admin';
import { DashboardPage } from './pages/Dashboard';
import { UsersPage } from './pages/Users';
import { ModerationPage } from './pages/Moderation';
import { ReportsPage } from './pages/Reports';
import { PlansPage } from './pages/Plans';
import { SettingsPage } from './pages/Settings';
import { PaymentConfigurationPage } from './pages/PaymentConfiguration';
import { FinancialPage } from './pages/Financial';
import { AuditPage } from './pages/Audit';
import {
  BranchDetailPage,
  BusinessDetailPage,
  BusinessDirectoryPage,
  StaffDetailPage,
  StaffDirectoryPage,
} from './pages/Directories';

const NAV = [
  { to: '/', label: 'Dashboard', end: true },
  { to: '/moderation', label: 'Listing moderation' },
  { to: '/reports', label: 'Reported listings' },
  { to: '/users', label: 'Users & owners' },
  { to: '/businesses', label: 'Businesses & branches' },
  { to: '/staff', label: 'Branch staff' },
  { to: '/plans', label: 'Plans & pricing' },
  { to: '/financial', label: 'Financial reports' },
  { to: '/settings/payments', label: 'Payment configuration' },
  { to: '/settings', label: 'Platform settings', end: true },
  { to: '/audit', label: 'Audit log' },
];

export default function App() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [initialising, setInitialising] = useState(true);

  const signOut = useCallback(() => {
    session.clear();
    setUser(null);
  }, []);

  useEffect(() => {
    setExpiredHandler(signOut);
  }, [signOut]);

  useEffect(() => {
    if (!session.read()) {
      setInitialising(false);
      return;
    }

    adminApi
      .me()
      .then((profile) => {
        // The panel is for administrators only. Anyone else is signed straight back out,
        // and every API call behind it is independently guarded on the server.
        if (profile.roles.includes('SuperAdmin')) setUser(profile);
        else signOut();
      })
      .catch(signOut)
      .finally(() => setInitialising(false));
  }, [signOut]);

  const [changingPassword, setChangingPassword] = useState(false);

  if (initialising) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen onSignedIn={setUser} />;
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          RoomKart <span>Admin</span>
        </div>

        <nav aria-label="Admin sections">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end}>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <div className="small text-truncate">{user.email}</div>
          <button
            type="button"
            className="btn btn-sm btn-outline-light w-100 mt-2"
            onClick={() => setChangingPassword(true)}
          >
            Change password
          </button>
          <button type="button" className="btn btn-sm btn-outline-light w-100 mt-2" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      {changingPassword && <ChangePasswordDialog onClose={() => setChangingPassword(false)} />}

      <main className="admin-main">
        <Routes>
          <Route index element={<DashboardPage />} />
          <Route path="moderation" element={<ModerationPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="businesses" element={<BusinessDirectoryPage />} />
          <Route path="businesses/:businessId" element={<BusinessDetailPage />} />
          <Route path="branches/:branchId" element={<BranchDetailPage />} />
          <Route path="staff" element={<StaffDirectoryPage />} />
          <Route path="staff/:userId" element={<StaffDetailPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="financial" element={<FinancialPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="settings/payments" element={<PaymentConfigurationPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}


/**
 * Lets the signed-in administrator replace their own password.
 *
 * This exists so that changing a password never requires a shell. Typed into the browser
 * and posted over the API, neither the old nor the new value reaches a terminal, a shell
 * history file, a CI log or a server log - the API logs no request bodies, and nothing
 * here writes either field anywhere. The current password is required, so an unlocked
 * browser tab is not enough on its own.
 */
function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // The server is the authority on password strength. This only catches the mistake the
  // server cannot see, which is the two new values not matching.
  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('The two new passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      await adminApi.changeOwnPassword({ currentPassword, newPassword });

      // Cleared before the confirmation renders, so the values do not sit in component
      // state behind it.
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal d-block"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-password-title"
      style={{ background: 'rgba(0,0,0,.5)' }}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title h5" id="change-password-title">
              Change password
            </h2>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>

          {done ? (
            <>
              <div className="modal-body">
                <div className="alert alert-success mb-0">
                  Your password has been changed. Use the new one next time you sign in.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-primary" onClick={onClose}>
                  Done
                </button>
              </div>
            </>
          ) : (
            <form onSubmit={submit} autoComplete="off">
              <div className="modal-body">
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                  <label className="form-label" htmlFor="cp-current">
                    Current password
                  </label>
                  <input
                    id="cp-current"
                    type="password"
                    className="form-control"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="cp-new">
                    New password
                  </label>
                  <input
                    id="cp-new"
                    type="password"
                    className="form-control"
                    autoComplete="new-password"
                    minLength={8}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <div className="form-text">
                    At least 8 characters, with an upper-case letter, a lower-case letter and a digit.
                  </div>
                </div>

                <div className="mb-0">
                  <label className="form-label" htmlFor="cp-confirm">
                    Confirm new password
                  </label>
                  <input
                    id="cp-confirm"
                    type="password"
                    className={mismatch ? 'form-control is-invalid' : 'form-control'}
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {mismatch && <div className="invalid-feedback">The two new passwords do not match.</div>}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy || mismatch}>
                  {busy ? 'Changing...' : 'Change password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sign in, or register the first administrator.
 *
 * Registration is gated by a shared admin code, checked on the server. This screen only
 * collects it - it does not know the value and never receives it in any response.
 */
function LoginScreen({ onSignedIn }: { onSignedIn: (user: UserProfile) => void }) {
  const [mode, setMode] = useState<'signIn' | 'register'>('signIn');

  return (
    <main className="admin-login">
      <section className="admin-login__visual" aria-label="RoomKart administration">
        <div className="admin-brand">
          RoomKart <span>Admin</span>
        </div>
        <div className="admin-login__message">
          <span className="admin-login__eyebrow">ROOMKART ADMIN</span>
          <h1>Every detail, thoughtfully managed.</h1>
          <p>One clear view of the people, places, and moments that make RoomKart.</p>
        </div>
        <img src="/roomkart-admin-login.svg" alt="" className="admin-login__image" />
      </section>

      <section className="admin-login__form" aria-label="Administrator sign in">
        <div className="admin-login__card">
          <h1 className="h3 mb-2">Welcome back</h1>
          <p className="text-muted mb-4">Sign in to your RoomKart admin workspace.</p>

          <div className="btn-group w-100 mb-3" role="group" aria-label="Sign in or register">
            <button
              type="button"
              className={`btn btn-${mode === 'signIn' ? 'primary' : 'outline-primary'}`}
              onClick={() => setMode('signIn')}
            >
              Sign in
            </button>
            <button
              type="button"
              className={`btn btn-${mode === 'register' ? 'primary' : 'outline-primary'}`}
              onClick={() => setMode('register')}
            >
              Register
            </button>
          </div>

          {mode === 'signIn' ? (
            <SignInForm onSignedIn={onSignedIn} />
          ) : (
            <RegisterForm onSignedIn={onSignedIn} />
          )}
        </div>
      </section>
    </main>
  );
}

function SignInForm({ onSignedIn }: { onSignedIn: (user: UserProfile) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const response = await adminApi.login(email.trim(), password);

      if (!response.user.roles.includes('SuperAdmin')) {
        setError('This account does not have administrator access.');
        return;
      }

      session.write({ accessToken: response.accessToken, refreshToken: response.refreshToken });
      onSignedIn(response.user);
      navigate('/');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      {error && (
        <div className="alert alert-danger py-2 px-3" role="alert">
          {error}
        </div>
      )}

      <div className="mb-3">
        <label className="form-label" htmlFor="admin-email">
          Email
        </label>
        <input
          id="admin-email"
          type="email"
          className="form-control"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="admin-password">
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          className="form-control"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          required
        />
      </div>

      <button type="submit" className="btn btn-primary w-100" disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>

      <p className="small text-muted mt-3 mb-0">
        Administrator access only. Every action in this panel is recorded in the audit log.
      </p>
    </form>
  );
}

function RegisterForm({ onSignedIn }: { onSignedIn: (user: UserProfile) => void }) {
  const [adminCode, setAdminCode] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    // Checked here for a quick answer, and again on the server - which is the check
    // that counts, since this one can be bypassed.
    if (password !== confirmPassword) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const response = await adminApi.registerAdmin({
        adminCode: adminCode.trim(),
        fullName: fullName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        password,
        confirmPassword,
      });

      session.write({ accessToken: response.accessToken, refreshToken: response.refreshToken });
      onSignedIn(response.user);
      navigate('/');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate>
      {error && (
        <div className="alert alert-danger py-2 px-3" role="alert">
          {error}
        </div>
      )}

      {/* First and prominent: without it nothing else on this form matters. */}
      <div className="mb-3">
        <label className="form-label fw-semibold" htmlFor="admin-code">
          Admin code
        </label>
        <input
          id="admin-code"
          type="password"
          className="form-control form-control-lg"
          value={adminCode}
          onChange={(e) => setAdminCode(e.target.value)}
          autoComplete="off"
          required
        />
        <div className="form-text">
          Issued by the platform operator. Registration is refused without it.
        </div>
      </div>

      <hr />

      <div className="mb-3">
        <label className="form-label" htmlFor="register-name">
          Full name
        </label>
        <input
          id="register-name"
          className="form-control"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="register-email">
          Email
        </label>
        <input
          id="register-email"
          type="email"
          className="form-control"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="username"
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="register-phone">
          Mobile number
        </label>
        <input
          id="register-phone"
          className="form-control"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          inputMode="tel"
        />
        <div className="form-text">Optional. Ten digits.</div>
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="register-password">
          Password
        </label>
        <input
          id="register-password"
          type="password"
          className="form-control"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
        <div className="form-text">
          At least 8 characters, with a digit, a capital and a small letter.
        </div>
      </div>

      <div className="mb-3">
        <label className="form-label" htmlFor="register-confirm">
          Confirm password
        </label>
        <input
          id="register-confirm"
          type="password"
          className="form-control"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
        />
      </div>

      <button type="submit" className="btn btn-primary w-100" disabled={busy}>
        {busy ? 'Creating…' : 'Create administrator account'}
      </button>

      <p className="small text-muted mt-3 mb-0">
        The account is created with full platform administration rights, and the
        registration is recorded in the audit log.
      </p>
    </form>
  );
}

// =====================================================================================
// Shared presentation helpers used across the admin pages
// =====================================================================================

export function PageHeader({ title, description, actions }: { title: string; description?: string; actions?: ReactNode }) {
  return (
    <div className="admin-page-header d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
      <div>
        <h1 className="h4 mb-1">{title}</h1>
        {description && <p className="text-muted mb-0 small">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

export function Stat({ value, label, tone }: { value: string | number; label: string; tone?: 'warn' | 'ok' }) {
  return (
    <div className={`admin-stat ${tone ? `admin-stat--${tone}` : ''}`}>
      <div className="admin-stat__value">{value}</div>
      <div className="admin-stat__label">{label}</div>
    </div>
  );
}

export function Alert({ message, variant = 'danger' }: { message: string | null; variant?: string }) {
  if (!message) return null;

  return (
    <div className={`alert alert-${variant} py-2 px-3`} role="alert">
      {message}
    </div>
  );
}

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="admin-skeleton mb-2" />
      ))}
      <span className="visually-hidden">Loading…</span>
    </div>
  );
}

export function Pager<T>({
  result,
  onPage,
}: {
  result: { page: number; totalPages: number; totalCount: number; pageSize: number; hasNext: boolean; hasPrevious: boolean; items: T[] };
  onPage: (page: number) => void;
}) {
  if (result.totalPages <= 1) return null;

  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <span className="small text-muted">
        Page {result.page} of {result.totalPages} · {result.totalCount} total
      </span>

      <div className="btn-group">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={!result.hasPrevious}
          onClick={() => onPage(result.page - 1)}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={!result.hasNext}
          onClick={() => onPage(result.page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
